import {
  MILESTONE_CANTONAL_SUPPORT,
  MILESTONE_STATUS,
  MILESTONE_STATUS_AFTER_SUPPORT,
  MILESTONE_TYPE,
} from 'src/shared/constants/milestone-constants'
import { FinalProjectReportExportMenu } from 'src/shared/menu/FinalProjectReportExportMenu'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useParams } from 'react-router'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useClient } from 'urql'
import { Utils } from 'src/shared/utils/Utils'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { PageLayout } from 'src/shared/layout/PageLayout'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { resolveImplementationDetailsRoot } from 'src/routing/routing-utils'
import { MilestoneFinalReportChart } from 'src/shared/charts/milestone-final-report/MilestoneFinalReportChart'
import { Milestone, Query_Root } from 'src/@types/graphql'
import { milestoneConclusionDataQuery } from 'src/screens/shared/implementation/details/milestoneQueries'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { AccordionDetails, AccordionSummary, Box, Card, CardContent, css, Typography } from '@mui/material'
import { HtmlRenderer } from 'src/shared/presentation/HtmlRenderer'
import { Section } from 'src/shared/presentation/Section'
import styled from 'styled-components'
import { CheckIcon, ExpandMoreIcon } from 'src/shared/icons/Icons'
import { DefaultSectionTypography } from 'src/shared/presentation/DefaultSectionTypography'
import { S } from 'src/shared/styled/S'
import {
  sortMilestoneCantonalSupport,
  sortMilestoneStatusesAfterSupport,
} from 'src/screens/shared/implementation/details/milestone/utils/MilestoneUtils'
import { ReadOnlyTextField } from 'src/shared/presentation/ReadOnly'
import { resolveConclusionRatingColor } from 'src/shared/utils/StylingUtils'
import { EditButton } from 'src/shared/button/Buttons'
import { ROUTES } from 'src/routing/routes'
import { UserContext } from 'src/user/UserContext'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useMilestoneTitle } from 'src/screens/shared/implementation/details/milestone/MilestoneTitleContext'

interface Props {
  baseUrl: '/pf-kap' | '/pf-pgv'
}

const SelectionContainer = styled(Box)`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`

const TitleStyled = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`

const SelectedListItem = styled(Typography)<{ $selected?: boolean }>`
  color: inherit;
  display: grid;
  grid-auto-flow: column;
  grid-gap: ${({ theme }) => theme.spacing(1)};
  align-items: center;
  min-height: 1.75rem;
  width: fit-content;
  ${({ $selected }) =>
    $selected &&
    css`
      &:not(:last-child) {
        margin-bottom: ${({ theme }) => theme.spacing(0.625)};
      }
    `}
`

const AvailableSelectionsContainer = styled(Box)`
  color: ${({ theme }) => theme.colors.text.disabled};
  margin-left: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`

export const FinalReportConclusionPage = ({ baseUrl }: Props) => {
  const { projectId, milestoneId } = useParams()

  const navigate = useDelayedNavigate()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const process = Utils.resolveProcess(baseUrl)
  const milestonePath = Utils.resolveMilestonePath(MILESTONE_TYPE.FINAL_REPORT)
  const title = useMilestoneTitle()

  const { user } = useContext(UserContext)
  const { getMessage } = useMessageSource()

  const [milestoneConclusion, setMilestoneConclusion] = useState<Milestone>()
  const [chartBase64Image, setChartBase64Image] = useState<string>()
  const [canEdit, setCanEdit] = useState<boolean>()

  const fetchData = useCallback(async () => {
    const { data } = await urqlClient
      .query<{ milestone_by_pk: Query_Root['milestone_by_pk'] }>(milestoneConclusionDataQuery, { milestoneId })
      .toPromise()

    if (data?.milestone_by_pk) {
      setMilestoneConclusion(data.milestone_by_pk)
      setCanEdit(data.milestone_by_pk.status !== MILESTONE_STATUS.DONE && Utils.isInternalRole(user.roles))
    } else {
      notificationService.operationFailed()
    }
  }, [urqlClient, milestoneId, user.roles, notificationService])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const milestoneCantonalHashChange = milestoneConclusion?.cantonal_support.join('')
  const milestoneCantonalSupport = useMemo(
    () => milestoneConclusion?.cantonal_support ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [milestoneCantonalHashChange],
  )

  const milestoneStatusAfterSupportHashChange = milestoneConclusion?.status_after_support.join('')
  const milestoneStatusAfterSupport = useMemo(
    () => milestoneConclusion?.status_after_support ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [milestoneStatusAfterSupportHashChange],
  )

  const onBackHandler = () => {
    navigate(resolveImplementationDetailsRoot(baseUrl).params({ projectId }))
  }

  const onEditStatusAfterSupport = () => {
    navigate(
      ROUTES.PfKapFinalReportMilestoneRoot.nested.ConclusionStatusAfterSupportEdit.params({
        projectId,
        milestoneId,
        milestonePath,
      }),
    )
  }

  const onEditCantonalSupport = () => {
    navigate(
      ROUTES.PfKapFinalReportMilestoneRoot.nested.ConclusionCantonalSupportEdit.params({
        projectId,
        milestoneId,
        milestonePath,
      }),
    )
  }

  const onEditRating = () => {
    navigate(
      ROUTES.PfKapFinalReportMilestoneRoot.nested.ConclusionRatingEditPage.params({
        projectId,
        milestoneId,
        milestonePath,
      }),
    )
  }

  const onEditConclusion = () => {
    navigate(
      ROUTES.PfKapFinalReportMilestoneRoot.nested.ConclusionEditPage.params({
        projectId,
        milestoneId,
        milestonePath,
      }),
    )
  }

  return (
    <ScreenLayout
      title={title}
      onBack={onBackHandler}
      actions={<FinalProjectReportExportMenu process={process} chartBase64Image={chartBase64Image} />}
    >
      <PageLayout>
        <>
          {milestoneConclusion && (
            <>
              <Section
                id={'rating'}
                title={getMessage('label.milestone.conclusion.rating')}
                helpAndInstructions={
                  <HelpAndInstructions labelKey={`label.help.milestone.conclusion.rating.${process.toLowerCase()}`} />
                }
                actionButton={<EditButton origin="content" color="primary" hidden={!canEdit} onClick={onEditRating} />}
              >
                <Card>
                  <CardContent>
                    <ReadOnlyTextField text={getMessage('label.conclusion.rating')}>
                      {milestoneConclusion.rating && (
                        <Typography color={resolveConclusionRatingColor(milestoneConclusion.rating)}>
                          {milestoneConclusion.rating &&
                            getMessage(`label.milestone.conclusion.${milestoneConclusion.rating}`)}
                        </Typography>
                      )}
                    </ReadOnlyTextField>
                  </CardContent>
                </Card>
              </Section>

              {/* 'Status after support' section */}
              <Section
                id={'status_after_support'}
                title={getMessage('label.milestone.conclusion.status_after_support')}
                actionButton={
                  <EditButton origin="content" color="primary" hidden={!canEdit} onClick={onEditStatusAfterSupport} />
                }
                helpAndInstructions={
                  <HelpAndInstructions
                    labelKey={`label.help.milestone.conclusion.status_after_support.${process.toLowerCase()}`}
                  />
                }
              >
                <Card>
                  <CardContent>
                    <SelectionContainer>
                      <TitleStyled variant="h6">{getMessage('label.your.selection')}</TitleStyled>

                      {milestoneStatusAfterSupport && milestoneStatusAfterSupport.length > 0 ? (
                        <>
                          {sortMilestoneStatusesAfterSupport(milestoneStatusAfterSupport).map((status: any) => (
                            <SelectedListItem
                              $selected={true}
                              variant="body2"
                              key={getMessage(`label.milestone.conclusion.status_after_support.${status}`)}
                            >
                              <CheckIcon /> {getMessage(`label.milestone.conclusion.status_after_support.${status}`)}
                            </SelectedListItem>
                          ))}
                        </>
                      ) : (
                        <DefaultSectionTypography type="SELECTION" $standAlone={false} />
                      )}
                    </SelectionContainer>

                    <S.Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">{getMessage('label.available.selections')}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <AvailableSelectionsContainer>
                          {Object.keys(MILESTONE_STATUS_AFTER_SUPPORT).map((key) => (
                            <SelectedListItem variant="body2" key={key} color="inherit">
                              {getMessage(`label.milestone.conclusion.status_after_support.${key}`)}
                            </SelectedListItem>
                          ))}
                        </AvailableSelectionsContainer>
                      </AccordionDetails>
                    </S.Accordion>
                  </CardContent>
                </Card>
              </Section>

              {/* 'Cantonal support' section */}
              <Section
                id={'cantonal_support'}
                title={getMessage('label.milestone.conclusion.cantonal_support')}
                helpAndInstructions={
                  <HelpAndInstructions
                    labelKey={`label.help.milestone.conclusion.cantonal_support.${process.toLowerCase()}`}
                  />
                }
                actionButton={
                  <EditButton origin="content" color="primary" hidden={!canEdit} onClick={onEditCantonalSupport} />
                }
              >
                <Card>
                  <CardContent>
                    <SelectionContainer>
                      <TitleStyled variant="h6">{getMessage('label.your.selection')}</TitleStyled>

                      {milestoneCantonalSupport && milestoneCantonalSupport.length > 0 ? (
                        <>
                          {sortMilestoneCantonalSupport(milestoneCantonalSupport).map((status: any) => (
                            <SelectedListItem
                              $selected={true}
                              variant="body2"
                              key={getMessage(`label.milestone.conclusion.cantonal_support.${status}`)}
                            >
                              <CheckIcon /> {getMessage(`label.milestone.conclusion.cantonal_support.${status}`)}
                            </SelectedListItem>
                          ))}
                        </>
                      ) : (
                        <DefaultSectionTypography type="SELECTION" $standAlone={false} />
                      )}
                    </SelectionContainer>

                    <S.Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">{getMessage('label.available.selections')}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <AvailableSelectionsContainer>
                          {Object.keys(MILESTONE_CANTONAL_SUPPORT).map((key) => (
                            <SelectedListItem variant="body2" key={key} color="inherit">
                              {getMessage(`label.milestone.conclusion.cantonal_support.${key}`)}
                            </SelectedListItem>
                          ))}
                        </AvailableSelectionsContainer>
                      </AccordionDetails>
                    </S.Accordion>
                  </CardContent>
                </Card>
              </Section>

              {/* Conclusion section */}
              <Section
                id="conclusion"
                title={getMessage('label.milestone.conclusion')}
                helpAndInstructions={
                  <HelpAndInstructions labelKey={`label.help.milestone.conclusion.${process.toLowerCase()}`} />
                }
                actionButton={
                  <EditButton origin="content" color="primary" hidden={!canEdit} onClick={onEditConclusion} />
                }
              >
                {milestoneConclusion?.conclusion && (
                  <Card>
                    <CardContent>
                      <HtmlRenderer html={milestoneConclusion?.conclusion} />
                    </CardContent>
                  </Card>
                )}
              </Section>
            </>
          )}
        </>
      </PageLayout>

      <MilestoneFinalReportChart
        setChartBase64Image={setChartBase64Image}
        style={{ visibility: 'hidden', display: 'none' }}
      />
    </ScreenLayout>
  )
}
